import { DialogComponent } from '@theme/dialog';

/**
 * @typedef {{ type?: string, image?: string, fitScale?: number, description?: string, fitNotes?: string, hasFitGraphic?: boolean, fitGraphicOverallFit?: string, fitGraphicLow?: string, fitGraphicMiddle?: string, fitGraphicHigh?: string, isShoesOrBelts?: boolean }} SizeGuideProductData
 */

/** @type {Window & { openSizeGuideForProduct?: (data: SizeGuideProductData) => void, sizeGuideOnDialogOpen?: () => void }} */
const win = window;

/** data-section attribute → target element id for header sg-tabs scroll */
const SECTION_ID_MAP = {
  'product-info': 'product-info',
  'size-chart': 'size-chart',
  'conversion-guide': 'size-chart',
  measure: 'sg-how-to-measure',
};

/** Number of segments in the fit graphic scale (mirrors snippets/s-fit-graphic.liquid) */
const FIT_GRAPHIC_SCALE_ITEMS = 5;

/** Scale item index (1-based) → label modifier, mirroring snippets/s-fit-graphic.liquid */
const FIT_GRAPHIC_LABEL_POSITIONS = { 1: 'left', 3: 'center', 5: 'right' };

/** Section element id → data-section for scroll-spy */
const SECTION_IDS_ORDER = ['product-info', 'size-chart', 'sg-how-to-measure'];
const SECTION_ID_TO_DATA_SECTION = {
  'product-info': 'product-info',
  'size-chart': 'size-chart',
  'sg-how-to-measure': 'measure',
};

/**
 * A custom element that manages the size guide drawer (dialog).
 * Handles [data-open-size-guide] clicks and window.openSizeGuideForProduct().
 *
 * @extends {DialogComponent}
 */
class SizeGuideDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.#boundOpenForProduct = (productData) => this.open(productData);
    win.openSizeGuideForProduct = this.#boundOpenForProduct;
    document.addEventListener('click', this.#handleDocumentClick, true);
    this.addEventListener('click', this.#handleHeaderTabClick);
    this.addEventListener('dialog:open', this.#handleDialogOpen);
    this.addEventListener('dialog:close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleDocumentClick, true);
    this.removeEventListener('click', this.#handleHeaderTabClick);
    this.removeEventListener('dialog:open', this.#handleDialogOpen);
    this.removeEventListener('dialog:close', this.#handleDialogClose);
    this.#removeScrollSpy();
    this.#disconnectCustomersSayObserver();
    if (win.openSizeGuideForProduct === this.#boundOpenForProduct) {
      delete win.openSizeGuideForProduct;
    }
  }

  /**
   * Find the drawer instance associated with this trigger (next sibling or parent’s drawer).
   * @param {Element} trigger
   * @returns {SizeGuideDrawerComponent | null}
   */
  static #drawerForTrigger(trigger) {
    let next = trigger.nextElementSibling;
    while (next) {
      if (next.tagName?.toLowerCase() === 'size-guide-drawer-component')
        return /** @type {SizeGuideDrawerComponent} */ (next);
      next = next.nextElementSibling;
    }
    const inProductDetails = trigger
      .closest?.('.product-details')
      ?.querySelector?.('size-guide-drawer-component');
    const inParent = trigger.parentElement?.querySelector?.('size-guide-drawer-component');
    return /** @type {SizeGuideDrawerComponent | null} */ (inProductDetails ?? inParent ?? null);
  }

  /**
   * Removes JS-cloned Size & Fit tab + section (quick-add / mini-PDP). SSR markup has no clone marker.
   * @param {HTMLDialogElement} dialog
   */
  static #removeClonedProductInfo(dialog) {
    dialog
      .querySelector(
        '.sg-sticky-header .sg-tabs button[data-section="product-info"][data-sg-product-info-clone]',
      )
      ?.remove();
    dialog.querySelector('#product-info[data-sg-product-info-clone]')?.remove();
  }

  /**
   * Markup for the fit graphic, mirroring snippets/s-fit-graphic.liquid so the injected
   * (quick-add / PLP) graphic renders identically to the server-rendered (PDP) one.
   * @returns {string}
   */
  static #fitGraphicHtml() {
    let items = '';
    for (let i = 1; i <= FIT_GRAPHIC_SCALE_ITEMS; i++) {
      const position =
        FIT_GRAPHIC_LABEL_POSITIONS[/** @type {keyof typeof FIT_GRAPHIC_LABEL_POSITIONS} */ (i)];
      const label = position
        ? `<div class="fit-graphic__scale-item-label fit-graphic__scale-item-label--${position}"></div>`
        : '';
      items += `<div class="fit-graphic__scale-item-container"><div class="fit-graphic__scale-item"></div>${label}</div>`;
    }
    return `<div class="fit-graphic" hidden><p class="fit-graphic__title"></p><div class="fit-graphic__scale">${items}</div></div>`;
  }

  /**
   * @param {HTMLDialogElement} dialog
   */
  static #cloneProductInfoShell(dialog) {
    const tabTpl = dialog.querySelector('#sg-drawer-product-info-tab-template');
    const sectionTpl = dialog.querySelector('#sg-drawer-product-info-section-template');
    if (!tabTpl || !sectionTpl) return;
    const tabs = dialog.querySelector('.sg-sticky-header .sg-tabs');
    const content = dialog.querySelector('.sg-content');
    if (!tabs || !content) return;
    const tabBtn = tabTpl.content.querySelector('button[data-section="product-info"]');
    const sectionRoot = sectionTpl.content.querySelector('#product-info');
    if (!tabBtn || !sectionRoot) return;
    tabs.insertBefore(tabBtn.cloneNode(true), tabs.firstElementChild);
    content.insertBefore(sectionRoot.cloneNode(true), content.firstElementChild);
  }

  /**
   * @param {MouseEvent} e
   */
  #handleDocumentClick = (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const link = target?.closest?.('[data-open-size-guide]');
    if (!link) return;

    const drawer = SizeGuideDrawerComponent.#drawerForTrigger(link);

    const hasFitGraphicAttr = link.getAttribute('data-has-fit-graphic');
    const productData = {
      type: link.getAttribute('data-product-type') ?? undefined,
      image: link.getAttribute('data-product-image') ?? undefined,
      fitScale: link.getAttribute('data-fit-scale')
        ? parseFloat(link.getAttribute('data-fit-scale'))
        : undefined,
      description: link.getAttribute('data-product-description') ?? undefined,
      readCustomersSay: link.getAttribute('data-read-customers-say') ?? undefined,
      fitNotes: link.getAttribute('data-product-fit-notes') ?? undefined,
      fitGraphicOverallFit: link.getAttribute('data-fit-graphic-overall-fit') ?? undefined,
      fitGraphicLow: link.getAttribute('data-fit-graphic-low') ?? undefined,
      fitGraphicMiddle: link.getAttribute('data-fit-graphic-middle') ?? undefined,
      fitGraphicHigh: link.getAttribute('data-fit-graphic-high') ?? undefined,
      isShoesOrBelts: link.getAttribute('data-size-guide-excluded-product') === 'true',
      hasFitGraphic:
        hasFitGraphicAttr === null
          ? undefined
          : hasFitGraphicAttr === '' || hasFitGraphicAttr === 'true',
    };

    if (!drawer) {
      e.preventDefault();
      if (typeof win.openSizeGuideForProduct === 'function') {
        win.openSizeGuideForProduct(productData);
      }
      return;
    }

    if (drawer !== this) return;

    e.preventDefault();
    this.setProductData(productData);
    this.showDialog();
  };

  #handleDialogOpen = () => {
    const dialog = this.refs.dialog;
    if (!dialog) return;
    const scrollEl = dialog.querySelector('.sg-content');
    const isExcludedProduct = dialog.getAttribute('data-size-guide-excluded-product') === 'true';

    const fitNotesAttr = dialog.getAttribute('data-product-fit-notes') ?? '';
    const hasFitNotesFromData = fitNotesAttr !== '';
    const hiddenTypesCsv =
      ',' + (dialog.getAttribute('data-size-guide-hidden-product-types') || '') + ',';
    const currentType = dialog.getAttribute('data-product-type') || '';
    const isFlatImageType = currentType !== '' && hiddenTypesCsv.includes(',' + currentType + ',');
    const isShoesOrBelts = isExcludedProduct;

    let productInfoEl = dialog.querySelector('#product-info');
    const isSsrProductInfo =
      productInfoEl instanceof HTMLElement &&
      !productInfoEl.hasAttribute('data-sg-product-info-clone');

    if (!hasFitNotesFromData && !isSsrProductInfo) {
      SizeGuideDrawerComponent.#removeClonedProductInfo(dialog);
    } else if (hasFitNotesFromData && !productInfoEl) {
      SizeGuideDrawerComponent.#cloneProductInfoShell(dialog);
      productInfoEl = dialog.querySelector('#product-info');
    }

    if (typeof win.sizeGuideOnDialogOpen === 'function') {
      win.sizeGuideOnDialogOpen();
    }
    const unitToggle = dialog.querySelector('.sg-unit-toggle .sg-toggle');
    if (unitToggle && typeof win.sizeGuideIsMetric !== 'undefined') {
      unitToggle.setAttribute('aria-pressed', win.sizeGuideIsMetric ? 'true' : 'false');
    }
    // Inject product description and fit notes into drawer (same dialog that has data attributes)
    const productDetailsEl = dialog.querySelector('#sg-product-details-content');
    if (productDetailsEl) {
      const desc = dialog.getAttribute('data-product-description');
      // Wrap in inner div so content stays inside accordion (avoids stray closing tags in description breaking DOM)
      productDetailsEl.innerHTML =
        desc != null ? '<div class="sg-product-details-body">' + desc + '</div>' : '';
    }
    // Fall back to class selector when ID elements are absent (static Liquid branch rendered when page product has fit_notes)
    const productInfoContent =
      dialog.querySelector('#sg-product-info-content') ||
      dialog.querySelector('.sg-product-info-content');
    const productInfoSection = dialog.querySelector('#product-info');
    const productMainImage = /** @type {HTMLImageElement | null} */ (
      dialog.querySelector('#sg-product-main-image') ||
        dialog.querySelector('.sg-product-main-image')
    );
    const isSsrProductInfoFinal =
      productInfoSection instanceof HTMLElement &&
      !productInfoSection.hasAttribute('data-sg-product-info-clone');
    const hasFitNotes = hasFitNotesFromData || isSsrProductInfoFinal;
    const shouldShowProductInfo = !isFlatImageType && !isShoesOrBelts && hasFitNotes;

    const productImage = dialog.getAttribute('data-product-image');
    const fitScaleRaw = dialog.getAttribute('data-fit-scale');
    const hasFitGraphicAttr = dialog.getAttribute('data-has-fit-graphic');
    const fitGraphicOverallFit = dialog.getAttribute('data-fit-graphic-overall-fit') || '';
    /** Label position → value; null when the attribute is absent (server-rendered labels stay untouched) */
    const fitGraphicLabelValues = [
      ['left', dialog.getAttribute('data-fit-graphic-low')],
      ['center', dialog.getAttribute('data-fit-graphic-middle')],
      ['right', dialog.getAttribute('data-fit-graphic-high')],
    ];
    const hasImage = productImage != null && productImage !== '';
    const fitScale = fitScaleRaw != null && fitScaleRaw !== '' ? Number(fitScaleRaw) : NaN;
    const hasFitScale = Number.isFinite(fitScale);
    let fitGraphic = dialog.querySelector('.fit-graphic');
    const hasFitGraphic =
      hasFitGraphicAttr === '' ||
      hasFitGraphicAttr === 'true' ||
      (hasFitGraphicAttr == null && fitGraphicOverallFit !== '');

    if (productInfoContent && hasFitNotesFromData) {
      productInfoContent.innerHTML = fitNotesAttr ? fitNotesAttr : '';
    }
    if (!fitGraphic && hasFitGraphic) {
      const fitGraphicParent = dialog.querySelector('#product-info .sg-product-info__text');
      if (fitGraphicParent) {
        fitGraphicParent.insertAdjacentHTML(
          'beforeend',
          SizeGuideDrawerComponent.#fitGraphicHtml(),
        );
        fitGraphic = fitGraphicParent.lastElementChild;
      }
    }
    const fitGraphicScale = fitGraphic?.querySelector('.fit-graphic__scale');
    const fitGraphicTitle = fitGraphic?.querySelector('.fit-graphic__title');
    if (fitGraphicTitle && fitGraphicOverallFit !== '') {
      fitGraphicTitle.textContent = `${fitGraphicOverallFit}:`;
    }
    for (const [position, value] of fitGraphicLabelValues) {
      if (value == null) continue;
      const label = fitGraphic?.querySelector(`.fit-graphic__scale-item-label--${position}`);
      if (label) label.textContent = value;
    }
    if (fitGraphic) {
      if (hasFitGraphic) {
        fitGraphic.removeAttribute('hidden');
      } else {
        fitGraphic.setAttribute('hidden', '');
      }
    }
    if (fitGraphicScale) {
      fitGraphicScale.classList.remove('scale--1', 'scale--2', 'scale--3', 'scale--4', 'scale--5');
      // No fit scale → no highlighted bar, matching snippets/s-fit-graphic.liquid
      const activeScale = hasFitScale
        ? Math.max(1, Math.min(FIT_GRAPHIC_SCALE_ITEMS, Math.round(fitScale)))
        : 0;
      if (activeScale > 0) fitGraphicScale.classList.add(`scale--${activeScale}`);
      // The highlighted bar lives on the scale item itself (see snippets/s-fit-graphic.liquid)
      const scaleItems = fitGraphicScale.querySelectorAll('.fit-graphic__scale-item');
      scaleItems.forEach((item, index) => {
        item.classList.toggle('fit-graphic__scale-item--active', index + 1 === activeScale);
      });
    }
    if (productMainImage) {
      if (hasImage) {
        productMainImage.src = productImage;
        // Clear srcset so the browser uses the updated src rather than the previously-rendered responsive set
        productMainImage.srcset = '';
        productMainImage.removeAttribute('hidden');
      } else {
        productMainImage.removeAttribute('src');
        productMainImage.srcset = '';
        productMainImage.setAttribute('hidden', '');
      }
    }
    if (productInfoSection) {
      if (shouldShowProductInfo) {
        productInfoSection.classList.remove('sg-product-info--empty');
        productInfoSection.removeAttribute('hidden');
        const tab = dialog.querySelector('.sg-tabs button[data-section="product-info"]');
        if (tab) tab.classList.remove('sg-product-info-tab--hidden');
      } else {
        productInfoSection.classList.add('sg-product-info--empty');
        productInfoSection.setAttribute('hidden', '');
        const tab = dialog.querySelector('.sg-tabs button[data-section="product-info"]');
        if (tab) tab.classList.add('sg-product-info-tab--hidden');
      }
    }
    const unitToggleWrap = dialog.querySelector('.sg-unit-toggle');
    if (unitToggleWrap) {
      if (isFlatImageType) {
        unitToggleWrap.setAttribute('hidden', '');
      } else {
        unitToggleWrap.removeAttribute('hidden');
      }
    }
    // After product-info visibility + tab unhide (injected fit notes / image from mini-PDP, etc.)
    const sizeChartTab = dialog.querySelector('.sg-tabs button[data-section="size-chart"]');
    const productInfoTab = isExcludedProduct
      ? null
      : dialog.querySelector(
          '.sg-tabs button[data-section="product-info"]:not(.sg-product-info-tab--hidden)',
        );
    const allTabs = dialog.querySelectorAll('.sg-tabs button[data-section]');
    allTabs.forEach((t) => t.classList.remove('active'));
    const tabToActivate =
      (isExcludedProduct ? sizeChartTab : null) ||
      productInfoTab ||
      sizeChartTab ||
      dialog.querySelector('.sg-tabs button[data-section]');
    if (tabToActivate) {
      tabToActivate.classList.add('active');
    }
    if (scrollEl) {
      scrollEl.scrollTop = 0;
      requestAnimationFrame(() => {
        scrollEl.scrollTop = 0;
      });
    }
    this.#setupScrollSpy();
    this.#populateCustomersSay();
  };

  /** @type {MutationObserver | null} */
  #customersSayObserver = null;

  /**
   * Extract the "Overall fit" value from the Yotpo custom questions widget on the page
   * and populate the "Customers Say" section in the drawer.
   * Yotpo renders asynchronously, so we observe the DOM if the value isn't available yet.
   */
  #populateCustomersSay() {
    this.#disconnectCustomersSayObserver();
    const dialog = this.refs.dialog;
    if (!dialog) return;

    const customersSayEl =
      dialog.querySelector('#sg-customers-say') || dialog.querySelector('.sg-customers-say');
    const valueEl =
      dialog.querySelector('#sg-customers-say-value') ||
      dialog.querySelector('.sg-customers-say__value');
    if (!customersSayEl || !valueEl) return;

    // Reset to hidden on each open so it never shows stale data
    customersSayEl.classList.add('sg-customers-say--hidden');
    valueEl.textContent = '';

    if (dialog.getAttribute('data-read-customers-say') != null) {
      // Try to read the value immediately
      const value = SizeGuideDrawerComponent.#readOverallFitValue();
      if (value) {
        valueEl.textContent = value;
        customersSayEl.classList.remove('sg-customers-say--hidden');
        return;
      }

      // Yotpo may not have rendered yet — observe the page DOM for it
      const observeTarget = document.body;
      this.#customersSayObserver = new MutationObserver(() => {
        const val = SizeGuideDrawerComponent.#readOverallFitValue();
        if (val) {
          valueEl.textContent = val;
          customersSayEl.classList.remove('sg-customers-say--hidden');
          this.#disconnectCustomersSayObserver();
        }
      });
      this.#customersSayObserver.observe(observeTarget, { childList: true, subtree: true });
    }

    // Stop observing after 10 seconds to avoid indefinite watching
    setTimeout(() => this.#disconnectCustomersSayObserver(), 10000);
  }

  #disconnectCustomersSayObserver() {
    if (this.#customersSayObserver) {
      this.#customersSayObserver.disconnect();
      this.#customersSayObserver = null;
    }
  }

  /**
   * Read the "Overall fit" value text from the Yotpo custom questions widget on the page.
   * Returns the value string (e.g. "True to size") or null if not found.
   * @returns {string | null}
   */
  static #readOverallFitValue() {
    const wrappers = document.querySelectorAll(
      '.yotpo-custom-questions-range-question-wrapper[type="Size"]',
    );
    for (const wrapper of wrappers) {
      const titleEl = wrapper.querySelector('.yotpo-custom-questions-range-question-title');
      if (titleEl && titleEl.textContent?.trim().toLowerCase() === 'overall fit') {
        const valueEl = wrapper.querySelector('.yotpo-custom-questions-range-question-value');
        const value = valueEl?.textContent?.trim();
        return value || null;
      }
    }
    return null;
  }

  #scrollSpyEl = /** @type {Element | null} */ (null);
  #scrollSpyHandler = () => this.#updateActiveTabFromScroll();

  #handleDialogClose = () => {
    const dialog = this.refs.dialog;
    if (dialog) SizeGuideDrawerComponent.#removeClonedProductInfo(dialog);
    this.#removeScrollSpy();
    this.#disconnectCustomersSayObserver();
  };

  #setupScrollSpy() {
    this.#removeScrollSpy();
    const dialog = this.refs.dialog;
    const scrollEl = dialog?.querySelector('.sg-content');
    if (!scrollEl) return;
    this.#scrollSpyEl = scrollEl;
    scrollEl.addEventListener('scroll', this.#scrollSpyHandler, { passive: true });
  }

  #removeScrollSpy() {
    if (!this.#scrollSpyEl) return;
    this.#scrollSpyEl.removeEventListener('scroll', this.#scrollSpyHandler);
    this.#scrollSpyEl = null;
  }

  /**
   * Set active state for sticky header tabs.
   * Allows one or multiple tabs to be active (range highlight behavior).
   * @param {string[]} dataSections
   */
  #setActiveHeaderTabs(dataSections) {
    const tabs = this.querySelectorAll('.sg-sticky-header .sg-tabs button[data-section]');
    const activeSet = new Set(dataSections);
    let hasVisibleActive = false;

    tabs.forEach((tab) => {
      const section = tab.getAttribute('data-section') || '';
      const isHidden = tab.classList.contains('sg-product-info-tab--hidden');
      const shouldActivate = !isHidden && activeSet.has(section);
      tab.classList.toggle('active', shouldActivate);
      if (shouldActivate) hasVisibleActive = true;
    });

    if (!hasVisibleActive) {
      const fallback = Array.from(tabs).find(
        (tab) => !tab.classList.contains('sg-product-info-tab--hidden'),
      );
      if (fallback) fallback.classList.add('active');
    }
  }

  /**
   * Update sticky header active tab based on which section is in view (scroll-spy).
   */
  #updateActiveTabFromScroll() {
    const dialog = this.refs.dialog;
    const scrollEl = dialog?.querySelector('.sg-content');
    if (!scrollEl) return;
    const scrollRect = scrollEl.getBoundingClientRect();
    const threshold = 80;
    const topRef = scrollRect.top + threshold;
    let activeDataSection = SECTION_ID_TO_DATA_SECTION['product-info'];
    let lastVisibleDataSection = activeDataSection;
    for (const id of SECTION_IDS_ORDER) {
      const section = dialog.querySelector(`#${CSS.escape(id)}`);
      if (!section || section.hasAttribute('hidden')) continue;
      lastVisibleDataSection =
        SECTION_ID_TO_DATA_SECTION[/** @type {keyof typeof SECTION_ID_TO_DATA_SECTION} */ (id)];
      const sectionRect = section.getBoundingClientRect();
      if (sectionRect.top <= topRef) {
        activeDataSection = lastVisibleDataSection;
      }
    }
    // If scrolled to bottom, activate the last visible section's tab
    if (Math.abs(scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight) < 2) {
      activeDataSection = lastVisibleDataSection;
    }

    const conversionContent = dialog.querySelector('#conversion-content');
    const isConversionTableVisible = conversionContent?.classList.contains('active');
    if (activeDataSection === 'size-chart') {
      this.#setActiveHeaderTabs([isConversionTableVisible ? 'conversion-guide' : 'size-chart']);
      return;
    }

    this.#setActiveHeaderTabs([activeDataSection]);
  }

  /**
   * Header sg-tabs: scroll to section and update active tab.
   * @param {MouseEvent} e
   */
  #handleHeaderTabClick = (e) => {
    const button =
      e.target instanceof Element
        ? e.target.closest('.sg-sticky-header .sg-tabs button[data-section]')
        : null;
    if (!button) return;

    const section = button.getAttribute('data-section');
    const targetId = section
      ? SECTION_ID_MAP[/** @type {keyof typeof SECTION_ID_MAP} */ (section)]
      : null;
    if (!targetId) return;

    const dialog = this.refs.dialog;
    const target = dialog?.querySelector(`#${CSS.escape(targetId)}`);
    if (!target) return;

    const scrollContainer = dialog?.querySelector('.sg-content');
    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      scrollContainer.scrollTo({
        top: scrollContainer.scrollTop + (targetRect.top - containerRect.top),
        behavior: 'smooth',
      });
    }

    // Keep size-chart sub-tabs aligned with the sticky header tab selection.
    if (section === 'conversion-guide') {
      const conversionSubTab = dialog?.querySelector('[data-chart-tab="conversion"]');
      if (conversionSubTab instanceof HTMLElement) {
        conversionSubTab.click();
      }
    } else if (section === 'size-chart') {
      const measurementsSubTab = dialog?.querySelector('[data-chart-tab="measurements"]');
      if (measurementsSubTab instanceof HTMLElement) {
        measurementsSubTab.click();
      }
    }

    if (section === 'conversion-guide') {
      this.#setActiveHeaderTabs(['conversion-guide']);
      return;
    }

    if (section === 'size-chart') {
      this.#setActiveHeaderTabs(['size-chart']);
      return;
    }

    this.#setActiveHeaderTabs([section]);
  };

  /**
   * Sets product data on the dialog element for the size guide script to read.
   * @param {{ type?: string, image?: string, fitScale?: number, description?: string, fitNotes?: string, readCustomersSay?: string, hasFitGraphic?: boolean, fitGraphicOverallFit?: string, fitGraphicLow?: string, fitGraphicMiddle?: string, fitGraphicHigh?: string, isShoesOrBelts?: boolean }} data
   */
  setProductData(data) {
    const dialog = this.refs.dialog;
    if (!dialog) return;
    if (data.type != null) {
      dialog.setAttribute('data-product-type', data.type);
    } else {
      dialog.removeAttribute('data-product-type');
    }
    if (data.image != null) {
      dialog.setAttribute('data-product-image', data.image);
      // Pre-load the image before the dialog opens to avoid a flash on reveal
      const img = /** @type {HTMLImageElement | null} */ (
        dialog.querySelector('#sg-product-main-image') ||
          dialog.querySelector('.sg-product-main-image')
      );
      if (img) {
        img.src = data.image;
        img.srcset = '';
      }
    } else {
      dialog.removeAttribute('data-product-image');
    }
    if (data.fitScale != null) {
      dialog.setAttribute('data-fit-scale', String(data.fitScale));
    } else {
      dialog.removeAttribute('data-fit-scale');
    }
    if (data.description != null) {
      dialog.setAttribute('data-product-description', data.description);
    } else {
      dialog.removeAttribute('data-product-description');
    }
    if (data.hasFitGraphic != null) {
      dialog.setAttribute('data-has-fit-graphic', data.hasFitGraphic ? 'true' : 'false');
    } else {
      dialog.removeAttribute('data-has-fit-graphic');
    }
    if (data.fitGraphicOverallFit != null) {
      dialog.setAttribute('data-fit-graphic-overall-fit', data.fitGraphicOverallFit);
    } else {
      dialog.removeAttribute('data-fit-graphic-overall-fit');
    }
    if (data.fitGraphicLow != null) {
      dialog.setAttribute('data-fit-graphic-low', data.fitGraphicLow);
    } else {
      dialog.removeAttribute('data-fit-graphic-low');
    }
    if (data.fitGraphicMiddle != null) {
      dialog.setAttribute('data-fit-graphic-middle', data.fitGraphicMiddle);
    } else {
      dialog.removeAttribute('data-fit-graphic-middle');
    }
    if (data.fitGraphicHigh != null) {
      dialog.setAttribute('data-fit-graphic-high', data.fitGraphicHigh);
    } else {
      dialog.removeAttribute('data-fit-graphic-high');
    }
    if (data.readCustomersSay != null) {
      dialog.setAttribute('data-read-customers-say', '');
    } else {
      dialog.removeAttribute('data-read-customers-say');
    }
    if (data.isShoesOrBelts != null) {
      dialog.setAttribute(
        'data-size-guide-excluded-product',
        data.isShoesOrBelts ? 'true' : 'false',
      );
    } else {
      dialog.removeAttribute('data-size-guide-excluded-product');
    }
    dialog.setAttribute('data-product-fit-notes', data.fitNotes || '');
  }

  /**
   * Opens the size guide with optional product data (for window.openSizeGuideForProduct).
   * @param {SizeGuideProductData} [productData]
   */
  open(productData) {
    if (productData) this.setProductData(productData);
    this.showDialog();
  }

  /** @type {((data: { type?: string, image?: string, fitScale?: number, description?: string }) => void) | undefined} */
  #boundOpenForProduct;
}

if (!customElements.get('size-guide-drawer-component')) {
  customElements.define('size-guide-drawer-component', SizeGuideDrawerComponent);
}
